import { useCallback, useMemo } from 'react';
import { PromoBanners } from '@/PromoBanner/constants';
import { useIsMobile } from '~/contexts/DeviceContext';
import usePriceDrop from '~/hooks/usePriceDrop';

type ImageAsset = `/search-assets/promo-banners/${string}`;

type BannerAssets = {
  backgroundImg: ImageAsset;
  vehicleImg: ImageAsset;
  iconBgColor: string;
  linkText: string;
  titleText: string;
};

export default function useGetBannerAssets(banner: PromoBanners): BannerAssets {
  const isMobile = useIsMobile();
  const { restrictedState } = usePriceDrop();

  const backgroundImg: ImageAsset =
    banner === PromoBanners.PriceDrop
      ? `/search-assets/promo-banners/green-swoosh${
          isMobile ? '-mobile' : ''
        }.jpg`
      : `/search-assets/promo-banners/blue-swoosh${
          isMobile ? '-mobile' : ''
        }.jpg`;

  const vehicleImg = useCallback((): ImageAsset => {
    switch (banner) {
      case PromoBanners.Earth:
        return `/search-assets/promo-banners/tesla-halo.png`;
      case PromoBanners.Ev:
        return `/search-assets/promo-banners/volvo-halo.png`;
      case PromoBanners.PriceDrop:
        return `/search-assets/promo-banners/pricedrop-image.png`;
    }
  }, [banner]);

  const iconBgColor: string =
    banner === PromoBanners.PriceDrop ? 'bg-green-2' : 'bg-[#a9e34b]';

  const linkText = useCallback((): string => {
    switch (banner) {
      case PromoBanners.Earth:
        return 'Shop All EVs and PHEVs';
      case PromoBanners.Ev:
        return 'Show Eligible Matches';
      case PromoBanners.PriceDrop:
        return `Shop All ${restrictedState ? 'New Prices' : 'Price Drops'}`;
    }
  }, [banner, restrictedState]);

  const titleText = useCallback((): string => {
    switch (banner) {
      case PromoBanners.Earth:
        return "We've got an EV for everyone, find yours!";
      case PromoBanners.Ev:
        return 'Save up to $4,000 on used EVs and PHEVs';
      case PromoBanners.PriceDrop:
        return restrictedState
          ? "We've updated prices on select vehicles!"
          : "We've lowered prices to give you more savings!";
    }
  }, [banner, restrictedState]);

  return useMemo(() => {
    return {
      backgroundImg,
      vehicleImg: vehicleImg(),
      iconBgColor,
      linkText: linkText(),
      titleText: titleText()
    };
  }, [backgroundImg, iconBgColor, linkText, titleText, vehicleImg]);
}
